import React from 'react';

import { Control } from '@vlabs/uikit';
import { get } from 'lodash';
import type { FieldValues } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';

import { numberInputValue } from '@vlabs/monokit/shared/lib/utils';
import { FormFieldGroup } from '@vlabs/monokit/shared/ui/form-field-group';

import { useStreamSettingsTranslation } from './i18n';
import type { StreamSettingsSyncResolver } from './types';
import { SynchronizeStatus } from '../sync/SynchronizeStatus';

interface AutorestartFieldsProps {
  /** Префикс пути полей в форме: 'autorestart' → autorestart.attempt_count. */
  namePrefix: string;
  getSyncStatus?: StreamSettingsSyncResolver;
}

// Поля автоперезапуска: количество попыток, задержка.
// Общие для настроек группы и источника.
function AutorestartFields({
  namePrefix,
  getSyncStatus = undefined,
}: AutorestartFieldsProps): React.JSX.Element {
  const { t } = useStreamSettingsTranslation();
  const {
    register,
    formState: { errors },
  } = useFormContext<FieldValues>();

  const errorMessage = (path: string): string | undefined => (
    get(errors, path)?.message as string | undefined
  );

  const syncIcon = (path: string): React.JSX.Element | undefined => {
    const synchronized = getSyncStatus?.(path);
    if (synchronized === undefined) return undefined;

    return <SynchronizeStatus synchronized={synchronized} />;
  };

  return (
    <React.Fragment>
      <FormFieldGroup.Item
        label={(
          <React.Fragment>
            {t('fields.attempt_count')}
            {syncIcon(`${namePrefix}.attempt_count`)}
          </React.Fragment>
        )}
        tooltip={t('tooltips.attempt_count')}
      >
        <Control.Input
          errorMessage={errorMessage(`${namePrefix}.attempt_count`)}
          errors={errors}
          max={32767}
          min={1}
          type="number"
          {...register(`${namePrefix}.attempt_count`, { setValueAs: numberInputValue })}
        />
      </FormFieldGroup.Item>

      <FormFieldGroup.Item
        label={(
          <React.Fragment>
            {t('fields.delay')}
            {syncIcon(`${namePrefix}.delay`)}
          </React.Fragment>
        )}
        tooltip={t('tooltips.delay')}
      >
        <Control.Input
          errorMessage={errorMessage(`${namePrefix}.delay`)}
          errors={errors}
          max={2147483647}
          min={1}
          type="number"
          {...register(`${namePrefix}.delay`, { setValueAs: numberInputValue })}
        />
      </FormFieldGroup.Item>
    </React.Fragment>
  );
}

export { AutorestartFields };
